import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiConfig } from "../config/api.config";
import logo from "../assets/DesarrollandoTerritorios.png";
import { Mail, Lock, LogIn, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

console.log("LOGIN NUEVO CARGADO - VERSION HACIENDA");

// Mapa de roles numéricos → nombre interno (solo logs)
const ROLE_MAP = {
  1: "solicitante",
  2: "planeacion",
  3: "hacienda",
  4: "juridica",
  5: "alcalde",
  6: "administrador",
  7: "presupuesto",
  8: "almacen",
  9: "validador",
  10: "firma_secretario",
  11: "administrador_junior",
  13: "hacienda_presupuesto",
};

// Rutas por rol
const ROLE_ROUTES = {
  1: "/menu",
  2: "/dashboard",
  3: "/menu",
  4: "/menu",
  5: "/menu",
  6: "/menu",
  7: "/menu",
  8: "/almacen/solicitudes",
  9: "/inexistencia/solicitudes",
  10: "/inexistencia/solicitudes",
  11: "/inexistencia/solicitudes",
  13: "/hacienda-presupuesto",
};

function getRoleId(user) {
  const raw =
    user?.rolId ??
    user?.role_id ??
    user?.roleId ??
    user?.rol_id ??
    user?.role ??
    null;

  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function getRoleName(user) {
  const byName = String(user?.rol ?? user?.role ?? user?.roleName ?? "")
    .trim()
    .toLowerCase();

  if (byName) return byName;

  const id = getRoleId(user);
  return id != null ? ROLE_MAP[id] || "" : "";
}

function getRedirectPathForUser(user) {
  const roleId = getRoleId(user);

  if (roleId === 13) return "/hacienda-presupuesto";
  if (roleId != null && ROLE_ROUTES[roleId]) return ROLE_ROUTES[roleId];

  return "/menu";
}

async function readResponsePayload(response) {
  const text = await response.text().catch(() => "");
  if (!text) return { data: null, text: "" };

  try {
    return { data: JSON.parse(text), text };
  } catch {
    return { data: null, text };
  }
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");
  const [loadingUi, setLoadingUi] = useState(false);

  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  const navigate = useNavigate();
  const { user, loading: authLoading, login: doLogin, logout: doLogout } =
    useAuth();

  const redirectedRef = useRef(false);

  const clearLocalSession = () => {
    try {
      localStorage.removeItem("auth_user");
      localStorage.removeItem("auth_token");
      sessionStorage.removeItem("auth_user");
      sessionStorage.removeItem("auth_token");
      sessionStorage.removeItem("post_login_path");

      // compatibilidad por si quedaron llaves antiguas
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
    } catch {
      // noop
    }
  };

  const logoutBackendIfExists = async () => {
    try {
      const logoutEndpoint =
        apiConfig?.endpoints?.logout || "/api/auth/logout/index.php";
      const logoutUrl = apiConfig.getUrl(logoutEndpoint);

      await fetch(logoutUrl, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" },
      }).catch(() => {});
    } catch {
      // no bloquea
    }
  };

  // Si ya hay sesión, saca al usuario de /login sin loops
  useEffect(() => {
    if (authLoading) return;

    const storedToken =
      localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");

    const storedUser =
      localStorage.getItem("auth_user") || sessionStorage.getItem("auth_user");

    if (!storedToken || !storedUser) {
      return;
    }

    if (!user) return;
    if (redirectedRef.current) return;

    const target = getRedirectPathForUser(user);

    if (window.location.pathname === target) {
      redirectedRef.current = true;
      return;
    }

    redirectedRef.current = true;

    console.log(
      `Redirigiendo usuario ya autenticado (${getRoleName(user)} / ID=${getRoleId(
        user
      )}) → ${target}`
    );

    window.location.replace(target);
  }, [authLoading, user]);

  const forceRedirect = (userObj) => {
    const target = getRedirectPathForUser(userObj);
    sessionStorage.setItem("post_login_path", target);

    // Redirección dura para evitar que la SPA conserve una vista vieja
    window.location.replace(target);
  };

  const persistSession = (userObj, tokenValue) => {
    const storage = rememberMe ? localStorage : sessionStorage;

    storage.setItem("auth_user", JSON.stringify(userObj));
    storage.setItem("auth_token", tokenValue || "session");

    const other = rememberMe ? sessionStorage : localStorage;
    other.removeItem("auth_user");
    other.removeItem("auth_token");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoadingUi(true);
    redirectedRef.current = false;

    try {
      await logoutBackendIfExists();
      clearLocalSession();

      const loginEndpoint =
        apiConfig?.endpoints?.login || "/api/auth/login/index.php";
      const loginUrl = apiConfig.getUrl(loginEndpoint);

      const response = await fetch(loginUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      });

      const { data, text } = await readResponsePayload(response);

      console.log("Respuesta del servidor (parsed):", data);
      if (!data && text) console.log("Respuesta del servidor (raw):", text);

      if (!response.ok) {
        const msg =
          data?.error ||
          data?.message ||
          (text
            ? `Error (HTTP ${response.status}): ${text.slice(0, 250)}`
            : `Error de autenticación (HTTP ${response.status})`);
        throw new Error(msg);
      }

      const userFromApi = data?.user || data?.data?.user || null;
      if (!userFromApi) {
        throw new Error("Respuesta de autenticación inválida (no llegó 'user').");
      }

      const tokenValue = data?.token || "session";

      persistSession(userFromApi, tokenValue);

      if (typeof doLogin === "function") {
        doLogin(userFromApi, tokenValue);
      }

      if (userFromApi?.needsPasswordChange) {
        navigate("/reset-password", { replace: true });
        return;
      }

      console.log("userFromApi completo:", userFromApi);
      console.log("roleId detectado:", getRoleId(userFromApi));
      console.log("ruta detectada:", getRedirectPathForUser(userFromApi));

      console.log("userFromApi completo:", userFromApi);
console.log("roleId detectado:", getRoleId(userFromApi));
console.log("ROLE_ROUTES completo:", ROLE_ROUTES);
console.log("ROLE_ROUTES[13]:", ROLE_ROUTES[13]);
console.log("ruta final calculada:", getRedirectPathForUser(userFromApi));

      forceRedirect(userFromApi);
    } catch (err) {
      console.error("Error al iniciar sesión:", err);
      setError(
        err?.message ||
          "Credenciales inválidas. Verifique correo y contraseña."
      );
      clearLocalSession();
    } finally {
      setLoadingUi(false);
    }
  };

  const handleForceLogout = async () => {
    setError("");
    setLoadingUi(true);

    try {
      clearLocalSession();

      if (typeof doLogout === "function") {
        doLogout();
      }

      await logoutBackendIfExists();
      window.location.replace("/login");
    } finally {
      setLoadingUi(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoadingUi(true);

    try {
      const redirectUrl = `${window.location.origin}/reset-password`;

      const endpoint =
        apiConfig?.endpoints?.requestPasswordReset ||
        "/api/auth/request-password-reset/index.php";

      const requestUrl = apiConfig.getUrl(endpoint);

      const response = await fetch(requestUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ email: resetEmail.trim(), redirectUrl }),
      });

      const { data, text } = await readResponsePayload(response);

      if (!response.ok) {
        const msg =
          data?.message ||
          data?.error ||
          (text
            ? `Error (HTTP ${response.status}): ${text.slice(0, 250)}`
            : `Error al solicitar restablecimiento (HTTP ${response.status})`);
        throw new Error(msg);
      }

      setResetEmailSent(true);
      setShowResetForm(false);
    } catch (err) {
      console.error(err);
      setError(
        "No se pudo enviar el correo. Verifique el email e intente nuevamente."
      );
    } finally {
      setLoadingUi(false);
    }
  };

  const isBusy = loadingUi || authLoading;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        alignItems: "center",
        background: "linear-gradient(to bottom, #1e3a8a, rgb(199, 204, 223))",
      }}
    >
      <div
        style={{
          display: "flex",
          flex: 1,
          width: "100%",
          justifyContent: "center",
          alignItems: "center",
          padding: "2rem",
        }}
      >
        <div
          style={{
            maxWidth: "400px",
            width: "100%",
            backgroundColor: "white",
            borderRadius: "1rem",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              padding: "1rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img
              src={logo || "/placeholder.svg"}
              alt="Logo"
              style={{ height: "200px", width: "auto", marginBottom: "0.5rem" }}
            />

            {resetEmailSent ? (
              <div style={{ textAlign: "center", padding: "1rem" }}>
                <h2
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    marginBottom: "1rem",
                  }}
                >
                  Correo Enviado
                </h2>
                <p style={{ marginBottom: "1rem" }}>
                  Se ha enviado un enlace de restablecimiento de contraseña a su
                  correo.
                </p>
                <button
                  onClick={() => {
                    setResetEmailSent(false);
                    setResetEmail("");
                  }}
                  style={{
                    backgroundColor: "#1e40af",
                    color: "white",
                    padding: "0.75rem 1rem",
                    borderRadius: "0.5rem",
                    border: "none",
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  Volver al inicio de sesión
                </button>
              </div>
            ) : showResetForm ? (
              <div style={{ width: "100%", padding: "1rem" }}>
                <h2
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    textAlign: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  Restablecer Contraseña
                </h2>
                <p
                  style={{
                    fontSize: "0.875rem",
                    color: "#6b7280",
                    textAlign: "center",
                    marginBottom: "1rem",
                  }}
                >
                  Ingrese su correo para recibir el enlace
                </p>

                {error && (
                  <div
                    style={{
                      backgroundColor: "#fef2f2",
                      color: "#b91c1c",
                      padding: "0.75rem",
                      borderRadius: "0.5rem",
                      marginBottom: "1rem",
                      width: "100%",
                      textAlign: "center",
                    }}
                  >
                    <p
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AlertCircle size={16} style={{ marginRight: "0.5rem" }} />
                      {error}
                    </p>
                  </div>
                )}

                <form onSubmit={handleResetPassword} style={{ width: "100%" }}>
                  <div style={{ marginBottom: "1.5rem" }}>
                    <label
                      htmlFor="resetEmail"
                      style={{
                        display: "block",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#374151",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Correo Electrónico
                    </label>
                    <div style={{ position: "relative" }}>
                      <div
                        style={{
                          position: "absolute",
                          left: "0.75rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                        }}
                      >
                        <Mail size={20} color="#9ca3af" />
                      </div>
                      <input
                        id="resetEmail"
                        type="email"
                        required
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="ejemplo@correo.com"
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          paddingLeft: "2.5rem",
                          borderRadius: "0.5rem",
                          border: "1px solid #d1d5db",
                          backgroundColor: "#f9fafb",
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                      type="button"
                      onClick={() => setShowResetForm(false)}
                      style={{
                        flex: 1,
                        backgroundColor: "#f3f4f6",
                        color: "#374151",
                        padding: "0.75rem 1rem",
                        borderRadius: "0.5rem",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isBusy}
                      style={{
                        flex: 1,
                        backgroundColor: "#1e40af",
                        color: "white",
                        padding: "0.75rem 1rem",
                        borderRadius: "0.5rem",
                        border: "none",
                        cursor: "pointer",
                        opacity: isBusy ? "0.7" : "1",
                      }}
                    >
                      {isBusy ? "Enviando..." : "Enviar"}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <>
                <h2
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    textAlign: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  Iniciar Sesión
                </h2>
                <p
                  style={{
                    fontSize: "0.875rem",
                    color: "#6b7280",
                    textAlign: "center",
                    marginBottom: "1rem",
                  }}
                >
                  Ingrese sus credenciales para continuar
                </p>

                {error && (
                  <div
                    style={{
                      backgroundColor: "#fef2f2",
                      color: "#b91c1c",
                      padding: "0.75rem",
                      borderRadius: "0.5rem",
                      marginBottom: "1rem",
                      width: "100%",
                      textAlign: "center",
                    }}
                  >
                    <p
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AlertCircle size={16} style={{ marginRight: "0.5rem" }} />
                      {error}
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                  <div style={{ marginBottom: "1.5rem" }}>
                    <label
                      htmlFor="email"
                      style={{
                        display: "block",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#374151",
                        marginBottom: "0.5rem",
                        textAlign: "center",
                      }}
                    >
                      Correo Electrónico
                    </label>
                    <div style={{ position: "relative" }}>
                      <div
                        style={{
                          position: "absolute",
                          left: "0.75rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                        }}
                      >
                        <Mail size={20} color="#9ca3af" />
                      </div>
                      <input
                        id="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ejemplo@correo.com"
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          paddingLeft: "2.5rem",
                          borderRadius: "0.5rem",
                          border: "1px solid #d1d5db",
                          backgroundColor: "#f9fafb",
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: "1.5rem" }}>
                    <label
                      htmlFor="password"
                      style={{
                        display: "block",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#374151",
                        marginBottom: "0.5rem",
                        textAlign: "center",
                      }}
                    >
                      Contraseña
                    </label>
                    <div style={{ position: "relative" }}>
                      <div
                        style={{
                          position: "absolute",
                          left: "0.75rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                        }}
                      >
                        <Lock size={20} color="#9ca3af" />
                      </div>
                      <input
                        id="password"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          paddingLeft: "2.5rem",
                          borderRadius: "0.5rem",
                          border: "1px solid #d1d5db",
                          backgroundColor: "#f9fafb",
                        }}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        style={{ height: "1rem", width: "1rem" }}
                      />
                      <label
                        htmlFor="remember-me"
                        style={{
                          marginLeft: "0.5rem",
                          fontSize: "0.875rem",
                          color: "#374151",
                        }}
                      >
                        Recordarme
                      </label>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowResetForm(true)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#1e40af",
                        fontSize: "0.875rem",
                        cursor: "pointer",
                        textDecoration: "underline",
                        padding: 0,
                      }}
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={isBusy}
                    style={{
                      width: "100%",
                      backgroundColor: "#1e40af",
                      color: "white",
                      padding: "0.75rem 1rem",
                      borderRadius: "0.5rem",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      position: "relative",
                      cursor: "pointer",
                      border: "none",
                      opacity: isBusy ? "0.7" : "1",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        left: "1rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <LogIn size={20} color="#93c5fd" />
                    </span>
                    {isBusy ? "Iniciando sesión..." : "Iniciar Sesión"}
                  </button>
                </form>

                <button
                  type="button"
                  onClick={handleForceLogout}
                  disabled={isBusy}
                  style={{
                    marginTop: "0.75rem",
                    width: "100%",
                    backgroundColor: "#f3f4f6",
                    color: "#111827",
                    padding: "0.65rem 1rem",
                    borderRadius: "0.5rem",
                    border: "1px solid #d1d5db",
                    cursor: "pointer",
                    opacity: isBusy ? "0.7" : "1",
                  }}
                >
                  Cambiar usuario / Cerrar sesión
                </button>
              </>
            )}

            <div style={{ marginTop: "2rem", textAlign: "center" }}>
              <p style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                Sistema de Gestión de Contratos - Alcaldía Municipal
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}